import React, { useState, useEffect, useRef } from 'react';
import { Badge } from 'react-bootstrap';
import EventSystem from './EventSystem';
import GlobalFunction from './globalFunction';
import './css/textModule.css';
import RmToolTip from './rmToolTip';
import { Tile, Title, Body } from './style/tile.style';
import ReactHtmlParser from 'react-html-parser';

export default function TextDisplayModule({ parameters, onSendMessage }) {
    const containerRef = useRef(null);
    const fontSizeCheckerRef = useRef(null);
    const timerIDRef = useRef(null);

    const storageKey = parameters.config.module_name + '_' + parameters.config.event_screen_module_id;
    const [message, setMessage] = useState('');
    const [fontSize, setFontSize] = useState(() => JSON.parse(localStorage.getItem(storageKey)) ?? 28);
    const [badgeVisible, setBadgeVisible] = useState(false);
    const [headingFontSize, setHeadingFontSize] = useState(0);

    function updateDimensions() {
        if (!containerRef.current) return;
        const headingWidth = containerRef.current.clientWidth - 80;
        let fs = GlobalFunction.getFontSize(parameters.config.description, headingWidth, 30, fontSizeCheckerRef.current);
        if (fs > 22) fs = 22;
        setHeadingFontSize(fs);
    }

    useEffect(() => {
        function showMessage(data) {
            if (parameters.config.event_grouping_seq !== data.event_grouping_seq.toString()) return;
            if (data.action === 'clear') {
                setMessage('');
            } else if (data.action === 'message') {
                let messageText = data.message;
                if (typeof data.messageHTML === 'string' && data.messageHTML.length > 0) {
                    messageText = ReactHtmlParser(data.messageHTML);
                }
                setMessage(messageText);
                if (data.flash) {
                    EventSystem.publish('FlashScreen', {
                        sourceModuleName: 'FlashScreen',
                        event_grouping_seq: 0,
                        customerID: parameters.userProfile.customer_id,
                        siteID: parameters.userProfile.site_id,
                        eventID: parameters.config.event_id,
                        action: 'start',
                    });
                }
            }
        }

        EventSystem.subscribe('TextControlModule', showMessage);
        window.addEventListener('resize', updateDimensions);
        updateDimensions();

        onSendMessage({
            action: 'getCurrentTextMessage',
            sourceModuleName: 'TextControlModule',
            event_grouping_seq: parameters.config.event_grouping_seq,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: parameters.config.event_id,
            message: '',
        });

        return () => {
            EventSystem.unsubscribe('TextControlModule', showMessage);
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);

    function triggerBadgeHide(newSize) {
        localStorage.setItem(storageKey, JSON.stringify(newSize));
        if (timerIDRef.current) clearTimeout(timerIDRef.current);
        timerIDRef.current = setTimeout(() => setBadgeVisible(false), 4000);
    }

    function increaseFont() {
        const newSize = fontSize + 1;
        setFontSize(newSize);
        setBadgeVisible(true);
        triggerBadgeHide(newSize);
    }

    function decreaseFont() {
        const newSize = fontSize - 1;
        setFontSize(newSize);
        setBadgeVisible(true);
        triggerBadgeHide(newSize);
    }

    const bodyHeight = parameters.containerHeight.container - 2 - 37 - 2;

    const zoomInStyle = { paddingLeft: '4px', paddingTop: '7px', position: 'absolute', display: 'inline-block', fontSize: '22px', top: '0px', right: '35px', height: '30px', width: '35px' };
    const zoomOutStyle = { paddingLeft: '4px', paddingTop: '7px', position: 'absolute', display: 'inline-block', fontSize: '22px', top: '0px', right: '0px', height: '30px', width: '35px' };
    const badgeStyle = { paddingTop: '5px', paddingBottom: '5px', fontSize: 'small', position: 'absolute', top: '4px', right: '80px', display: badgeVisible ? 'block' : 'none' };

    return (
        <Tile ref={containerRef} height={parameters.containerHeight.container - 2 + 'px'}>
            <Title fontSize={headingFontSize + 'px'}>
                <span style={{ paddingLeft: '5px' }}>{parameters.config.description}</span>
                <RmToolTip itemText="add_circle_outline" itemType="icon" placement="bottom"
                    onClick={increaseFont}
                    style={zoomInStyle}
                    toolTipText={<span>Increase<br />Font Size</span>} />
                <RmToolTip itemText="remove_circle_outline" itemType="icon" placement="bottom"
                    onClick={decreaseFont}
                    style={zoomOutStyle}
                    toolTipText={<span>Decrease<br />Font Size</span>} />
                <Badge style={badgeStyle}>{fontSize}</Badge>
            </Title>
            <Body fontSize={fontSize + 'px'} height={bodyHeight + 'px'} overflow="auto">
                {message}
            </Body>
            <div style={{ position: 'fixed', top: '-2000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }}
                ref={fontSizeCheckerRef} />
        </Tile>
    );
}
